import React, { useState } from 'react';
import { authAPI } from '../../api/client';
import type { LoginRequest, RegisterRequest } from '../../types';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import './Auth.css';

interface AuthProps {
  onAuthSuccess: () => void;
}

export const Auth: React.FC<AuthProps> = ({ onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [loginForm, setLoginForm] = useState<LoginRequest>({
    email: '',
    password: ''
  });

  const [registerForm, setRegisterForm] = useState<RegisterRequest>({
    email: '',
    password: '',
    fullName: ''
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(loginForm);
      // セッションCookieが設定され、ユーザー情報が返される
      if (response.userId) {
        // authAPI.loginで既にユーザー情報は保存されているので、
        // ここでは重複して保存しない
        
        // 少し遅延を入れてから遷移（状態更新を確実にするため）
        console.log('Login successful, response:', response);
        setTimeout(() => {
          onAuthSuccess();
        }, 100);
      } else {
        setError('ログインに失敗しました');
      }
    } catch (error) {
      setError('ログインに失敗しました。メールアドレスとパスワードを確認してください。');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.register(registerForm);
      
      // セッションCookieが設定され、ユーザー情報が返される
      if (response.userId) {
        // authAPI.registerで既にユーザー情報は保存されているので、
        // ここでは重複して保存しない
        
        // 少し遅延を入れてから遷移（状態更新を確実にするため）
        console.log('Registration successful, response:', response);
        setTimeout(() => {
          onAuthSuccess();
        }, 100);
      } else {
        setError('ユーザー登録に失敗しました');
      }
    } catch (error) {
      setError('ユーザー登録に失敗しました。入力内容を確認してください。');
      console.error('Register error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Evaloid</h1>
        <p className="auth-subtitle">AI観点抽出・評価システム</p>
        
        <div className="auth-tabs">
          <button 
            className={`auth-tab ${isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(true)}
          >
            ログイン
          </button>
          <button 
            className={`auth-tab ${!isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(false)}
          >
            新規登録
          </button>
        </div>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        {isLogin ? (
          <form onSubmit={handleLogin} className="auth-form">
            <Input
              type="email"
              placeholder="メールアドレス"
              value={loginForm.email}
              onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              required
            />
            <Input
              type="password"
              placeholder="パスワード"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              required
            />
            <Button type="submit" disabled={loading}>
              {loading ? 'ログイン中...' : 'ログイン'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="auth-form">
            <Input
              type="text"
              placeholder="お名前"
              value={registerForm.fullName}
              onChange={(e) => setRegisterForm({ ...registerForm, fullName: e.target.value })}
              required
            />
            <Input
              type="email"
              placeholder="メールアドレス"
              value={registerForm.email}
              onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
              required
            />
            <Input
              type="password"
              placeholder="パスワード（8文字以上）"
              value={registerForm.password}
              onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
              required
              minLength={8}
            />
            <Button type="submit" disabled={loading}>
              {loading ? '登録中...' : '新規登録'}
            </Button>
          </form>
        )}

        <div className="auth-footer">
          <p>
            {isLogin ? 'アカウントをお持ちでない方は' : '既にアカウントをお持ちの方は'}
            <button 
              type="button"
              className="auth-link"
              onClick={() => setIsLogin(!isLogin)}
            >
              {isLogin ? '新規登録' : 'ログイン'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};